/**
 * HeroImage Component
 *
 * A reusable component for displaying hero images (typically community logos) with consistent styling.
 * Supports loading states, error handling, and placeholder fallbacks.
 *
 * @example
 * // Basic usage
 * <HeroImage name="Gaming" logoUrl="https://example.com/logo.png" />
 *
 * @example
 * // With loading state
 * <HeroImage name="Gaming" logoUrl={community?.logoUrl} isLoading={loading} />
 *
 * @example
 * // Small variant for panels
 * <HeroImage name="Gaming" logoUrl={community?.logoUrl} size="small" />
 */

import { Shimmer } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import * as styles from './HeroImage.css';

export type HeroImageSize = 'default' | 'small' | 'card';

export interface HeroImageProps {
  /**
   * The name of the community (used for alt text and placeholder)
   */
  name: string;
  /**
   * The URL of the community logo image
   */
  logoUrl?: string | null;
  /**
   * Whether the component is in a loading state
   * @default false
   */
  isLoading?: boolean;
  /**
   * Size variant of the logo
   * @default 'default'
   */
  size?: HeroImageSize;
  /**
   * Custom alt text override (defaults to "{name} community image")
   */
  alt?: string;
  /**
   * Referrer policy for the image
   * @default 'no-referrer'
   */
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Fallback image URL to use when logoUrl fails or is not provided
   * Useful for card variants that want a background image fallback
   */
  fallbackImageUrl?: string;
  /**
   * Whether to show an overlay on fallback images (useful for cards)
   * @default false
   */
  showOverlayOnFallback?: boolean;
}

// Shimmer style defined outside component to avoid recreating on each render
const shimmerStyle = { width: '100%', height: '100%', borderRadius: '8px' };

/**
 * Builds the container class name based on size and custom className
 */
function buildContainerClassName(size: HeroImageSize, className?: string): string {
  return className ? `${styles.containerVariants[size]} ${className}` : styles.containerVariants[size];
}

/**
 * Builds the alt text for the image
 */
function buildAltText(name: string, alt?: string): string {
  return alt || (name ? `${name} community image` : 'Community image');
}

/**
 * Determines the image URL to display based on logo availability and error state
 */
function getImageUrl(logoUrl: string | null | undefined, fallbackImageUrl: string | undefined, imageError: boolean): string | null {
  if (logoUrl && !imageError) return logoUrl;
  if (fallbackImageUrl) return fallbackImageUrl;

  return null;
}

/**
 * Checks if the image is using a fallback
 */
function isUsingFallback(logoUrl: string | null | undefined, fallbackImageUrl: string | undefined, imageError: boolean): boolean {
  const hasValidLogo = Boolean(logoUrl) && !imageError;

  return Boolean(fallbackImageUrl && !hasValidLogo);
}

/**
 * HeroImage - Displays a hero image with loading and error states
 *
 * Features:
 * - Shimmer loading state
 * - Image error handling with automatic fallback
 * - Placeholder with community name when no image
 * - Multiple size variants (default, small, card)
 * - Resets error state when logoUrl changes
 */
export function HeroImage({
  name,
  logoUrl,
  isLoading = false,
  size = 'default',
  alt,
  referrerPolicy = 'no-referrer',
  className,
  fallbackImageUrl,
  showOverlayOnFallback = false,
}: HeroImageProps) {
  const [imageError, setImageError] = useState(false);

  // Reset error state when logoUrl changes (e.g., navigating between communities)
  useEffect(() => {
    setImageError(false);
  }, [logoUrl]);

  const containerClassName = buildContainerClassName(size, className);
  const heroImageAlt = buildAltText(name, alt);
  const imageUrl = getImageUrl(logoUrl, fallbackImageUrl, imageError);
  const hasFallback = isUsingFallback(logoUrl, fallbackImageUrl, imageError);

  // Render loading state
  if (isLoading) {
    return (
      <div className={containerClassName}>
        <Shimmer style={shimmerStyle} />
      </div>
    );
  }

  // Show image (either logo or fallback)
  if (imageUrl) {
    return (
      <div className={containerClassName}>
        <img alt={heroImageAlt} className={styles.image} referrerPolicy={referrerPolicy} src={imageUrl} onError={() => setImageError(true)} />
        {hasFallback && showOverlayOnFallback && <div className={styles.imageOverlay} />}
      </div>
    );
  }

  // Show placeholder
  return (
    <div className={containerClassName}>
      <div className={styles.placeholder}>
        <div>
          {name}
          <br />
          Community
        </div>
      </div>
    </div>
  );
}
